import type { BiConnectionGateway } from "./slice/bi-connection.js";
/**
 * The console's data seam, and the register of what the API does not yet document.
 *
 * WHY THIS FILE EXISTS. `@wetalk/api-client` is generated from `apps/api/openapi.yaml`:
 * one method per `operationId`, `send` is `protected`, and there is no escape hatch. That
 * is deliberate — **the console cannot call an endpoint the spec does not carry.** It is
 * also, today, a hard limit on what P10-01..03 can wire up: the assembled spec documents
 * the catalogue, agent_versions, connections, numbers, SIP, webhook endpoints, the five SSE
 * streams, conversations (defect D4), VoiceAgents (P11-01) and — since 2026-09-22 — the
 * customer identity entry points. What it still lacks is the register below.
 *
 * So the console is written against **this** interface rather than against the client, and
 * the interface is satisfied two ways:
 *
 *   `apiGateway`      — `src/api-gateway.ts`. Every method the spec covers is a real call
 *                       through the generated client. Every method it does not cover
 *                       raises {@link UndocumentedOperationError}, naming the operationId
 *                       that has to be added. Loud, never silent, never a default.
 *   `sandboxGateway`  — `src/sandbox-gateway.ts`. An in-memory implementation of the whole
 *                       interface, used by `pnpm dev` and by every suite, so the flows can
 *                       be exercised end to end before the routes land.
 *
 * Which one is wired is `VITE_WETALK_GATEWAY`, read and validated in `src/config.ts`. It is
 * explicit configuration with no default, exactly like `apps/api/src/server.ts`'s choice of
 * a fake authenticator for tests.
 *
 * {@link GATEWAY_GAP} is the machine-readable list of the gaps; `gateway.test.ts` asserts
 * every gap names an operation the assembled spec really is missing, so the register cannot
 * quietly go stale once the routes arrive.
 *
 * ── PER-AREA SLICES (plan-002 RB-03, design §15.11.4 IC-2) ───────────────────────────────
 * Eleven plan-002 units close the remaining gaps in parallel, so the remaining areas no longer
 * live in this file. Each has a slice under `./slice/<area>.ts` that
 *
 *   · declares its methods onto {@link ConsoleGateway} by DECLARATION MERGING, so the unit that
 *     owns the area owns its signatures without editing this file;
 *   · exports `<area>Gap`, its rows of the register — {@link GATEWAY_GAP} is their
 *     concatenation, and {@link gapFor} and {@link UndocumentedOperationError} are unchanged;
 *   · exports `<area>ApiSlice(client, context)`, which `api-gateway.ts` spreads.
 *
 * This file is the assembly and the shared view types; after RB-03 it does not change for a
 * Track C unit. `sandbox-gateway.ts` still implements the whole interface in one object.
 */
import type {
  AccountCapability,
  AccountRole,
  AgentTemplateField,
  ConversationDirection,
  CreditFigures,
  DiscoveryOutcome,
  MemberRole,
  Money,
  OutboundOrigin,
  TalkRateKind,
  WorkspaceCapability,
} from "@wetalk/domain";

// P12-03. The order book's views, its decoding and its fixture live with the screens that
// render them (`src/routes/agents/[id]/orders/`), the way `channel-view.ts` and
// `channel-sandbox.ts` live together — so this file grows one `extends` rather than ninety
// lines of a bounded context it does not otherwise know about.
import type { OrderBookGateway } from "../routes/agents/[id]/orders/order-book-gateway.js";
import type { ForwardFact } from "../routes/agents/new/opening-hours.js";
import type { ScopeGeneration } from "../scope/generation.js";

// plan-002 RB-03 (IC-2). Each area's rows of the register; the slices also merge their methods
// onto `ConsoleGateway`, which is why importing them here puts those methods on the interface
// for every consumer of this file.
import { accountGap } from "./slice/account.js";
import { agentDocumentGap } from "./slice/agent-document.js";
import { voiceAgentLifecycleGap } from "./slice/voice-agent-lifecycle.js";
import { voiceAgentEditGap } from "./slice/voice-agent-edit.js";
import { auditEntryGap } from "./slice/audit-entry.js";
import { billingCardGap } from "./slice/billing-card.js";
import { campaignGap } from "./slice/campaign.js";
import { complianceGap } from "./slice/compliance.js";
import { contactListGap } from "./slice/contact-list.js";
import { customerOrderPaymentGap } from "./slice/customer-order-payment.js";
import { doNotCallGap } from "./slice/do-not-call.js";
import { notificationGap } from "./slice/notification.js";
import { optimisationReviewGap } from "./slice/optimisation-review.js";
import { payoutGap } from "./slice/payout.js";
// plan-008 ST-UN: the account's own SIP trunks (IC-77) — the Numbers & SIP card, dialog, check,
// test calls and activity; ST-UA reads the same slice for the Channels tab.
import { sipTrunkGap } from "./slice/sip-trunk.js";
// plan-008 ST-UN (for ST-UA's Outbound tab): "ring a number now" through the account's trunk.
import { outboundConversationGap } from "./slice/outbound-conversation.js";
import type { GatewayGap } from "./slice/slice-context.js";
import type { LatestBuildView } from "./build-view.js";
import { surveyGap } from "./slice/survey.js";
import { testCallGap } from "./slice/test-call.js";
import { workspaceHomeGap } from "./slice/workspace-home.js";
// plan-014 REG (ED-15): the twelve customer areas of design §27.9.2 the console calls — each slice
// empty until its unit lands, so no plan-014 unit edits this file, `api-gateway.ts` or `sandbox-gateway.ts`.
import { accountTeamGap } from "./slice/account-team.js";
import { personalSecurityGap } from "./slice/personal-security.js";
import { accountSecurityGap } from "./slice/account-security.js";
import { domainJoinOfferGap } from "./slice/domain-join-offer.js";
import { billingAccountGap } from "./slice/billing-account.js";
import { billingProfileGap } from "./slice/billing-profile.js";
import { billingOperationGap } from "./slice/billing-operation.js";
import { commercialOfferGap } from "./slice/commercial-offer.js";
import { subscriptionGap } from "./slice/subscription.js";
import { entitlementGap } from "./slice/entitlement.js";
import { apiKeyGap } from "./slice/api-key.js"; // plan-014 C3
import { financialDocumentGap } from "./slice/financial-document.js";
import { postpaidGap } from "./slice/postpaid.js";
import { resourceShareGap } from "./slice/resource-share.js";
// plan-014 B3: the workspace area's plan-014 additions (funding, policies, invitation sign-up) — one slice, spread once.
import { workspaceManagementGap } from "./slice/workspace-management.js";
// plan-015 REG (ED-15): the new customer areas of design §28.4.2 the console calls — each slice empty until its unit
// lands, so no plan-015 unit edits this file, `api-gateway.ts` or `sandbox-gateway.ts`. QSET's survey-question-set
// slice was withdrawn by owner decision 2026-10-09 (a campaign asks its agent's live version).
import { documentPreviewGap } from "./slice/document-preview.js";
// plan-017 REG: the talk-rate area of design §29.9.1 the console reads — empty until WAPP-A lands, so no plan-017 unit
// edits this file (`api-gateway.ts` is WAPP-A's own in wave 3, design §29.16).
import { talkRateGap } from "./slice/talk-rate.js";
// plan-018 REG: the two new customer areas of design §30.4 the console calls — website-reading (API-AG) and handoff
// (API-HO) — each slice empty until its unit lands, so no plan-018 unit edits this file, `api-gateway.ts` or
// `sandbox-gateway.ts` (plan-018 §2.2).
import { websiteReadingGap } from "./slice/website-reading.js";
import { handoffGap } from "./slice/handoff.js";
// plan-019 REG: the help area of design §31.4.1–§31.4.2 the portal calls — empty until its unit lands, so no plan-019
// unit edits this file, `api-gateway.ts` or `sandbox-gateway.ts` (plan-019 §2.2).
import { helpGap } from "./slice/help.js";
// plan-021 REG: the pass-target area of design §33.13.1 the portal calls — empty until WAPP-PASS lands, so no plan-021
// unit edits this file, `api-gateway.ts` or `sandbox-gateway.ts` (plan-021 §2.2, ED-30).
import { voiceAgentPassGap } from "./slice/voice-agent-pass.js";
// plan-029 REG: the five areas of design §40.19 the portal calls — caller protection and blocked numbers (WAPP-PROT), the
// access list (WAPP-ACCESS, then WAPP-CONF), caller confirmation (WAPP-CONF) and visitor signing secrets (WAPP-VIS) —
// empty until each unit lands, so no plan-029 unit edits this file, `api-gateway.ts` or `sandbox-gateway.ts` (plan-029
// §2.2, ED-21).
import { callerProtectionGap } from "./slice/caller-protection.js";
import { blockedNumberGap } from "./slice/blocked-number.js";
import { partyAccessGap } from "./slice/party-access.js";
import { partyConfirmationGap } from "./slice/party-confirmation.js";
import { visitorSigningSecretGap } from "./slice/visitor-signing-secret.js";
// plan-030 WAPP-Q: the survey question editor (design §41.5) — empty: every operation is in API-Q's fragments.
import { surveyQuestionnaireGap } from "./slice/survey-questionnaire.js";
// plan-034 WAPP-APPSIGNIN: the consent page's mint of the app authorization code.
import { appSignInGap } from "./slice/app-sign-in.js";

// P14. Four screens' worth of view types each, so they live in their own modules and are
// re-exported below — the same arrangement as the order book, for the same reason: this file
// is the one every unit touches, and four bounded contexts of shapes do not belong in it. The
// methods that use them are declared in the slices (`./slice/*.ts`), not here.
// Re-exported so a screen imports its view types from `./api/gateway.js` like every other
// screen does, and never has to know which module they were declared in.
export type * from "./outbound-view.js";
export type * from "./suppression-view.js";
export type * from "./governance-view.js";
export type * from "./optimisation-review-view.js";

// ---------------------------------------------------------------------------------------
// The gap register — assembled from the slices (plan-002 RB-03, IC-2)
// ---------------------------------------------------------------------------------------

export { UndocumentedOperationError, type GatewayGap } from "./slice/slice-context.js";
export type { SliceContext } from "./slice/slice-context.js";
// Declared in RB-17's slice, because RB-17 changes it (C20); re-exported so every import of it
// from this file is unchanged.
export type { AcceptInvitationRequestView, InvitationPreviewView, SignUpOutcomeView, SignUpRequestView } from "./slice/account.js";

/**
 * Every refused operation, in one list: the concatenation of each area's own rows.
 *
 * Owners are plan-002 unit ids since RB-03 (C7: two rows named the wrong plan-001 unit). The
 * order is the order the rows had when they lived here.
 */
export const GATEWAY_GAP: readonly GatewayGap[] = [
  ...notificationGap,
  ...workspaceHomeGap,
  ...billingCardGap,
  ...testCallGap,
  ...contactListGap,
  ...campaignGap,
  ...surveyGap,
  ...doNotCallGap,
  ...complianceGap,
  ...auditEntryGap,
  ...optimisationReviewGap,
  ...accountGap,
  ...agentDocumentGap,
  ...voiceAgentLifecycleGap,
  ...voiceAgentEditGap,
  // plan-005 OP-36: empty — the order-payment acts are refused per call until OP-35's routes are
  // in the spec (`documentedOperationOf`), never registered here as a permanent gap.
  ...customerOrderPaymentGap,
  // plan-005 OP-38: empty — the payouts, destination, status and step-up operations are in the spec.
  ...payoutGap,
  // plan-008 ST-UN: empty — every trunk operation is in the spec.
  ...sipTrunkGap,
  // plan-008 ST-UN: empty — ST-AO documented start, read and end.
  ...outboundConversationGap,
  // plan-014 REG: empty until each area's unit lands.
  ...accountTeamGap,
  ...personalSecurityGap,
  ...accountSecurityGap,
  // plan-014 INT (owner answer 47): empty — both operations are in the spec.
  ...domainJoinOfferGap,
  ...billingAccountGap,
  ...billingProfileGap,
  ...billingOperationGap,
  ...commercialOfferGap,
  ...subscriptionGap,
  ...entitlementGap,
  ...financialDocumentGap,
  ...postpaidGap,
  ...resourceShareGap,
  // plan-014 B3: empty — every operation is in B3's fragments.
  ...workspaceManagementGap,
  // plan-014 C3: empty — every operation is in C3's fragments.
  ...apiKeyGap,
  // plan-015 REG: PRV's (QSET's withdrawn 2026-10-09).
  ...documentPreviewGap,
  // plan-017 REG: empty until WAPP-A lands.
  ...talkRateGap,
  // plan-018 REG: empty until API-AG and API-HO land.
  ...websiteReadingGap,
  ...handoffGap,
  // plan-019 REG: empty until the help slice's unit lands.
  ...helpGap,
  // plan-021 REG: empty until WAPP-PASS lands.
  ...voiceAgentPassGap,
  // plan-029 REG: empty until WAPP-PROT, WAPP-ACCESS, WAPP-CONF and WAPP-VIS land.
  ...callerProtectionGap,
  ...blockedNumberGap,
  ...partyAccessGap,
  ...partyConfirmationGap,
  ...visitorSigningSecretGap,
  // plan-030 WAPP-Q: empty.
  ...surveyQuestionnaireGap,
  // plan-034 WAPP-APPSIGNIN: empty — the operation is in API-APPAUTH's fragment.
  ...appSignInGap,
];

// `discoverIdentifier`, `signUp`, `logIn` and `currentAuthSession` were on this list until the
// identity entry points landed (`discoverIdentity`, `signUpPerson`, `logInPerson`,
// `getCurrentAuthSession`, plus `logOutPerson` for `logOut`). Their rows are GONE rather than
// commented out, for the reason given below.

// `createVoiceAgent`, `listVoiceAgents` and `getVoiceAgent` were on this list until P11-01
// landed `/v1/agents`. Their rows are GONE rather than commented out, for the same reason
// the three below are: `gateway.test.ts` fails while a gap names an operation the assembled
// spec now carries, so the register cannot quietly go stale.

// `getLiveSnapshot`, `listPastConversations` and `getPastConversation` were on this list
// until defect D4 landed `/v1/conversations`, `/v1/conversations/live` and
// `/v1/conversations/{conversation_id}`. Their rows are GONE rather than commented out:
// `gateway.test.ts` fails while a gap names an operation the assembled spec now carries, and
// the register is only worth having while it cannot go stale.

/**
 * Signed in, but not scoped to an account — so there is no workspace to open.
 *
 * The API issues an UNSCOPED auth_session in two cases (design §8.1): a person who belongs to
 * no account yet — every fresh sign-up, because sign-up creates a person and not an account —
 * and a person who belongs to several and has not chosen. {@link AuthSessionView} describes a
 * signed-in person *inside* a workspace, so neither case can be represented by it, and
 * inventing an account to fill it would be exactly the default design §9.1 forbids. The
 * gateway raises this instead, carrying the choices, so the screen that eventually handles
 * it (account creation in first-run; an account chooser) has what it needs.
 */
export class AuthSessionUnscopedError extends Error {
  override readonly name = "AuthSessionUnscopedError";
  readonly accountChoice: readonly {
    readonly accountId: string;
    readonly accountName: string;
    /**
     * The strongest workspace role held there (the legacy label), or `null` when the person holds
     * only an organization role there — Finance, say (plan-014 A2's account-only access).
     */
    readonly role: WorkspaceRoleView | null;
  }[];
  constructor(accountChoice: AuthSessionUnscopedError["accountChoice"]) {
    super(
      accountChoice.length === 0
        ? "You are signed in, but there is no business account attached to you yet. " +
            "Creating one is the first-run step, which wetalk cannot do from here yet."
        : "You are signed in and belong to more than one account. Choosing which one to " +
            "open is not available in the console yet.",
    );
    this.accountChoice = accountChoice;
  }
}

/**
 * An operator is acting as this person, and the customer API does not carry who or why.
 *
 * Design §8.7 obliges the console to show the operator's name and recorded reason. Those live
 * in the operator surface's own store, which the customer API may not read, so
 * `getCurrentAuthSession` carries the operator's id and the expiry and nothing more. Rather
 * than render a banner with an invented name or a blank reason, the console refuses to open.
 */
export class ImpersonationDetailUnavailableError extends Error {
  override readonly name = "ImpersonationDetailUnavailableError";
  constructor() {
    super(
      "A wetalk operator is signed in as this person, but the console cannot yet show who " +
        "they are or why. Refusing to open rather than hiding that from you.",
    );
  }
}

/** Looks a gap up by method name, so `api-gateway.ts` cannot invent one. */
export function gapFor(method: string): GatewayGap {
  const found = GATEWAY_GAP.find((one) => one.method === method);
  if (found === undefined) {
    throw new Error(`"${method}" is not a registered gateway gap; add it to GATEWAY_GAP.`);
  }
  return found;
}

// ---------------------------------------------------------------------------------------
// View types — console-shaped, not wire-shaped
// ---------------------------------------------------------------------------------------

export interface PersonView {
  readonly personId: string;
  readonly displayName: string;
  readonly emailAddress: string;
  readonly initials: string;
  readonly emailVerified: boolean;
}

export interface WorkspaceView {
  readonly workspaceId: string;
  readonly name: string;
  readonly initials: string;
  readonly memberCount: number;
  readonly spendThisMonth: Money;
}

/**
 * Who is signed in, and — the part that is easy to leave out — **on whose behalf**.
 *
 * `actor` exists because design §8.7 requires the customer to be able to see that a WeTalk
 * operator is acting as them. The enforcement half is already server-side and does not
 * depend on this: the grant's lifetime is fixed in the route and never read from a request,
 * every write is attributed to the operator through `app.current_operator`, nesting and
 * re-delegation are refused, and the audit entry outlives the session. What this adds is the
 * customer's ability to *know*, which is a transparency obligation rather than a control.
 *
 * It is a discriminated union rather than an `isImpersonating` boolean plus optional fields,
 * for the same reason the operator console has no such flag: a boolean can be forgotten and
 * the fields beside it can drift out of step with it. Here, an operator session cannot be
 * represented without its operator, its reason and its expiry.
 */
export type AuthSessionActorView =
  | { readonly kind: "self" }
  | {
      readonly kind: "operator";
      readonly operatorDisplayName: string;
      readonly reason: string;
      readonly expiresAt: string;
    };

/**
 * The organization states the API names (`account.state`). `suspended` and `closed` organizations stay
 * in the picker with their state, so a person can see where they lost access rather than wonder.
 */
/** AMENDED 2026-10-07 (plan-014 B4, migration 0102): `closing` — the organization's closure is under way. */
export type AccountStateView = "trial" | "active" | "past_due" | "suspended" | "closed" | "closing";

/** Where the console lands a context (design §27.6.3), as the API computes it. */
export type LandingView = "workspace" | "finance" | "organization" | "selection";

/** One workspace of an organization the person holds a membership in (plan-014 A2's `WorkspaceChoice`). */
export interface WorkspaceChoiceView {
  readonly workspaceId: string;
  readonly name: string;
  readonly initials: string;
  readonly state: string;
  readonly memberRole: WorkspaceRoleView;
  /** False for an archived workspace: it is listed nowhere a person could try to open it. */
  readonly enterable: boolean;
}

/** One organization the person can enter — through an organization role, workspaces, or both. */
export interface AccountChoiceView {
  readonly accountId: string;
  readonly name: string;
  readonly initials: string;
  readonly state: AccountStateView;
  /** The person's organization role there, or `null` for workspace-only access. */
  readonly accountRole: AccountRole | null;
  readonly workspace: readonly WorkspaceChoiceView[];
}

/**
 * What the context's roles hold — a LISTING for the console's navigation and affordances, never a
 * decision: the API judges every act against its target (plan-014 A3).
 */
export interface ContextCapabilityView {
  readonly account: readonly AccountCapability[];
  readonly workspace: readonly WorkspaceCapability[];
}

/** What every one of the three contexts carries. */
/**
 * Why `GET /v1/session` has just moved this auth_session (plan-014 FIX2, design §27.6.3): `membership_revoked` — the
 * person's membership of the workspace or organization it was in was removed, and the API took them to the next
 * one they can open (or the picker). Carried only by the read that made the move.
 */
export type AuthSessionNoticeView = "membership_revoked";

interface AuthSessionBase {
  readonly person: PersonView;
  /** Present only on the read that moved the auth_session; see {@link AuthSessionNoticeView}. */
  readonly notice?: AuthSessionNoticeView;
  readonly actor: AuthSessionActorView;
  /** The auth_session's scope generation (ED-12): requests carry it, a stale one is refused. */
  readonly scopeGeneration: number;
  /** Every organization the person can enter, re-read with the auth_session (access is never cached). */
  readonly accountChoice: readonly AccountChoiceView[];
  readonly landing: LandingView;
  readonly capability: ContextCapabilityView;
}

/**
 * Signed in, inside one organization AND one of its workspaces — every operational page's context.
 * `authSessionQuery` gives the console's pages this arm only (`../api/query.ts`).
 */
export interface WorkspaceAuthSessionView extends AuthSessionBase {
  readonly kind: "workspace_selected";
  readonly accountId: string;
  readonly accountName: string;
  readonly accountState: AccountStateView;
  /** The person's role in the organization too, or `null` for workspace-only access (plan-014 A3). */
  readonly accountRole: AccountRole | null;
  /**
   * The workspace the person is signed in to, by id. Its name, initials and figures are the
   * workspace listing's (`listWorkspaces`), read on their own: when the sign-in carried them, a
   * failed listing failed the sign-in too, and the whole console went with it (customer-portal
   * review 2026-09-25 #150). `shell/current-workspace.ts` finds it in the listing.
   */
  readonly workspaceId: string;
  /**
   * The signed-in person's role in this workspace (the auth_session's `member_role`). The console
   * reads it only to decide what to OFFER (`shell/member-capability.ts`); the API and the
   * database decide what is allowed (customer-portal review 2026-09-25 #134). Any of the seven.
   */
  readonly memberRole: WorkspaceRoleView;
}

/**
 * Signed in, inside one organization with NO workspace — Finance's normal state, and an Owner's or
 * Admin's on the Organization pages (FR12-AUTH-09: never a dummy workspace).
 */
export interface AccountOnlyAuthSessionView extends AuthSessionBase {
  readonly kind: "account_only";
  readonly accountId: string;
  readonly accountName: string;
  readonly accountState: AccountStateView;
  /** Never null: an organization without a workspace is entered only through an organization role. */
  readonly accountRole: AccountRole;
}

/** Signed in, no organization chosen — several to choose from, or none yet. */
export interface SelectionRequiredAuthSessionView extends AuthSessionBase {
  readonly kind: "selection_required";
}

/**
 * Who is signed in, in which of the three contexts (plan-014 A2/A4, design §27.6.1), and on whose
 * behalf ({@link AuthSessionActorView}).
 *
 * AMENDED 2026-10-05 (plan-014 A4): a discriminated union over the context's `kind`. Until then the
 * view described a person inside a workspace only, and an organization entered without a workspace
 * could not be represented. The workspace arm keeps every field the view had, so a page that reads
 * `authSessionQuery` (which selects that arm) is unchanged.
 */
export type AuthSessionView = WorkspaceAuthSessionView | AccountOnlyAuthSessionView | SelectionRequiredAuthSessionView;

/** A switch's answer: the new context, and the organization it left (for the post-switch notice). */
export interface SwitchedScopeView {
  readonly authSession: AuthSessionView;
  readonly previousAccountName: string | null;
}

/** Where a switch goes: an organization, and one of its workspaces or none (the organization alone). */
export interface ScopeTargetView {
  readonly accountId: string;
  readonly workspaceId: string | null;
}

/**
 * What the entry card sends beside each step (owner, 2026-10-17; ADR 0071): the sign-up trap's value (the unseen
 * field — empty for a person) and the solved identity challenge (`null` when the card could not solve one).
 */
export interface EntryGuardView {
  readonly entryMarker: string;
  readonly altcha: string | null;
}

/** `entry_marker` and `altcha` for a request body; nothing when no guard was given. */
export function entryGuardBodyOf(guard: EntryGuardView | undefined): Readonly<Record<string, string>> {
  if (guard === undefined) return {};
  return { entry_marker: guard.entryMarker, ...(guard.altcha === null ? {} : { altcha: guard.altcha }) };
}

export interface LogInRequestView {
  readonly emailAddress: string;
  readonly password: string;
  /** ADR 0071: the entry card's trap value and solved identity challenge; without it the api gateway solves one. */
  readonly guard?: EntryGuardView;
}

/** The billing alert banner's four states, as the design names them. */
export type BillingHealth = "healthy" | "low" | "declined" | "out-of-credit";

export interface BillingStateView {
  readonly health: BillingHealth;
  /** Available — the live balance admission refuses against (2026-10-04: labelled as such). */
  readonly balance: Money;
  /** Trial talk time left, in seconds. `null` once the trial is spent or superseded. */
  readonly trialSecondsRemaining: number | null;
  /**
   * What a new conversation would be told now, and why (2026-10-04), or `null` when it would be
   * admitted — the API's own sentence, so the banner never says "out of credit" while a
   * conversation in progress is what holds the credit.
   */
  readonly refusal: BillingRefusalView | null;
}

export interface NotificationView {
  readonly notificationId: string;
  readonly title: string;
  readonly dek: string;
  readonly whenText: string;
  readonly unread: boolean;
  /** The catalogue-style glyph name, resolved by `src/shell/glyph.ts`. */
  readonly iconName: string;
  /**
   * The console path the notice opens (`/agents/<id>` for a finished build), or `null` when it has
   * nowhere to go. A path, never a URL: `notification.link_path` refuses anything else.
   */
  readonly linkPath: string | null;
}

/** One of the four things first run asks for. */
export interface FirstRunTaskView {
  readonly code: "verify_email" | "create_voice_agent" | "connect_number" | "add_card";
  readonly title: string;
  readonly dek: string;
  readonly done: boolean;
  readonly actionLabel: string;
}

export interface FirstRunProgressView {
  readonly accountName: string;
  readonly createdAtText: string;
  readonly task: readonly FirstRunTaskView[];
}

export interface TierView {
  readonly tierId: string;
  readonly code: string;
  readonly name: string;
  /** How many conversations this tier may hold at once. Never bare "capacity" (C12). */
  readonly channelUnitCeiling: number;
  readonly ordinal: number;
}

/**
 * `GET /v1/numbers/availability` answers with one object, not a list of areas: whether new
 * numbers can be issued at all, and why not when they cannot.
 */
export interface NumberAvailabilityView {
  readonly available: boolean;
  readonly reason: string;
  readonly detail: string;
}

/**
 * One card on the template picker.
 *
 * `GET /v1/templates` returns **only available templates** — its SQL is
 * `WHERE available = true` — so the four `soon` rows the picker must show, disabled, are not
 * on that response and cannot be. The full six-row catalogue is read from
 * `@wetalk/domain`'s `AGENT_TEMPLATE_CATALOGUE`, which is the same data
 * `0006-p7-factory.sql` seeds, and the API's list is treated as the authority on which of
 * them is buildable right now. Nothing is invented: the two sources are merged, not guessed.
 */
export interface AgentTemplateCardView {
  readonly code: string;
  readonly name: string;
  readonly dek: string;
  readonly youAdd: string;
  /** The catalogue's own icon string, resolved by `src/shell/glyph.ts`. */
  readonly iconName: string;
  readonly available: boolean;
  readonly contentTitle: string;
  readonly contentDek: string;
  readonly fieldSet: readonly AgentTemplateField[];
  readonly defaultGreeting: string;
  readonly defaultName: string;
  readonly outcomeVocabulary: readonly string[];
  readonly successMetricLabel: string;
  /** The schema an extraction is validated against; the wizard reads one rule from it (one language). */
  readonly referenceSchema: string;
  /**
   * Who starts the conversation — `GET /v1/templates`' `direction` (plan-015 G7, migration 0118) for every template
   * the API returns, the compiled catalogue's for the coming-soon ones it cannot return. Never inferred.
   */
  readonly direction: ConversationDirection;
}

/**
 * `language_pack.maturity` (migration 0176, plan-026, owner 2026-10-21): a `beta` language is offered and chosen like
 * any other, and the portal marks it Beta with a note (`../shell/language-beta.tsx`). Which languages are beta is
 * this value, never a list in the portal.
 */
export const LANGUAGE_MATURITY = ["general", "beta"] as const;
export type LanguageMaturity = (typeof LANGUAGE_MATURITY)[number];

/** `voice.gender` (migration 0176): as the vendor's library lists it; `null` where it was not recorded. */
export const VOICE_GENDER = ["female", "male"] as const;
export type VoiceGender = (typeof VOICE_GENDER)[number];

export interface LanguagePackView {
  readonly code: string;
  readonly name: string;
  readonly nativeName: string;
  readonly maturity: LanguageMaturity;
  /**
   * `language_pack.default_voice_id` (0176): the voice preselected for this language when the person has chosen none
   * and it speaks the language (`languageVoiceOf`); `null` — Greek and English — preselects the first, as before.
   */
  readonly defaultVoiceId: string | null;
}

export interface VoiceView {
  readonly voiceId: string;
  readonly name: string;
  readonly description: string;
  readonly supportedLanguageCode: readonly string[];
  /** Shown beside the name where a voice is chosen; nothing is shown for `null`. */
  readonly gender: VoiceGender | null;
}

/** What the wizard collects, before anything is built. */
export interface VoiceAgentDraft {
  readonly agentTemplateCode: string;
  readonly displayName: string;
  /** `field.code` → what the customer typed, or the name of what they attached. */
  readonly fieldValue: Readonly<Record<string, string>>;
  readonly languageCode: readonly string[];
  readonly voiceId: string;
  readonly greeting: string;
  readonly numberChoice: "bring_my_own" | "new_number";
  readonly medium: readonly string[];
  readonly channelUnitCount: number;
  /** "Record calls" (RC-47). The wizard always states it; left out, the API writes its default, on. */
  readonly recordingEnabled?: boolean;
  /**
   * How callers pay, for a template with an order book: `cash_on_delivery`, `card_on_delivery`,
   * both, or none (the agent takes no payment), plus `online` beside them (plan-005 OP-38). Left
   * out (or `null`) for a template without one.
   */
  readonly paymentMethod?: readonly ("cash_on_delivery" | "card_on_delivery" | "online")[] | null;
  /** plan-014 GAP: "Start from a shared configuration" — the accepted share the new agent copies. */
  readonly resourceShareId?: string;
}

export interface CreatedVoiceAgentView {
  readonly voiceAgentId: string;
  readonly buildJobId: string;
}

/**
 * `draft` is the `agent_state` enum's first value and was missing here until P11-01 read the
 * table: a VoiceAgent exists as a row before its first build is queued, and a union that
 * could not express it would force the gateway to substitute another state for a real one.
 */
export type VoiceAgentState =
  | "draft"
  | "building"
  | "live"
  | "paused"
  | "scheduled"
  | "failed";

export interface VoiceAgentView {
  readonly voiceAgentId: string;
  readonly displayName: string;
  readonly agentTemplateCode: string;
  readonly agentTemplateName: string;
  readonly state: VoiceAgentState;
  /**
   * The first enabled voice endpoint, or `null`.
   *
   * Nullable since P11-01, because it genuinely is: a VoiceAgent is a row before a number is
   * bound to it, and the previous `string` forced whoever filled it to invent a placeholder
   * that reads like a number. The screens render "not connected yet" instead.
   */
  readonly partyNumber: string | null;
  /** `—` until the first version is published. */
  readonly liveAgentVersionLabel: string;
  /** `null` until the first version is published. Which row the Versions list marks live. */
  readonly liveAgentVersionId: string | null;
  /** What a publish or a restore must quote back, so a lost update is a 409. */
  readonly liveVersionSeq: number;
  /** The live version's configured languages, or the newest version's. */
  readonly languageCode: readonly string[];
  /**
   * The "Record calls" switch (plan-003 RC-47): the live version's answer, or the newest
   * version's before one is live. Changed with `setVoiceAgentRecording`, which publishes.
   */
  readonly recordingEnabled: boolean;
  /** The template's own word for what it counts — "Orders placed", "Bookings". */
  readonly successMetricLabel: string;
  /** Whole seconds of talk today — seconds, not minutes, so nothing rounds before `talkTimeText`. */
  readonly talkSecondToday: number;
  /** What billing charges for today: the free trial covers the seconds first (review #109). */
  readonly spendToday: Money;
  /** What billing charges for this month, trial seconds first (review #109). */
  readonly spendThisMonth: Money;
  /**
   * When the VoiceAgent was created (RFC 3339), where the source carries it — how the Agents
   * list tells two agents with the same name apart (review #108).
   */
  readonly createdAt?: string;
  /**
   * Who paused it (customer-portal review #106): `member` — its owner, with Pause; `operator` —
   * WeTalk, and only WeTalk resumes it; `null` — not paused.
   */
  readonly pausedBy: "member" | "operator" | null;
  /** Archived (#105): left out of the list and every picker, still readable by its id. */
  readonly archived: boolean;
  /** Whether any number or endpoint reaches it (#120) — "Live, not reachable" when it is live and not. */
  readonly reachable: boolean;
  /** Its newest build (#130): what it is doing, or why it failed. `null` before any build. */
  readonly latestBuild: LatestBuildView | null;
}

/** What `setVoiceAgentRecording` answers (plan-003 RC-47). */
export interface VoiceAgentRecordingView {
  readonly recordingEnabled: boolean;
  /** The sequence a later publish must quote: moved by one when a version was published. */
  readonly liveVersionSeq: number;
  /** `false` when the live version already had this value and nothing was published. */
  readonly changed: boolean;
}

export interface DashboardFiguresView {
  readonly greetingName: string;
  readonly todayText: string;
  readonly liveConversationCount: number;
  /** Whole seconds of talk today — seconds, not minutes, so nothing rounds before `talkTimeText`. */
  readonly talkSecondToday: number;
  readonly spendToday: Money;
  /**
   * `null` when there was no inbound conversation today (C21, RB-03): a percentage of nothing is
   * not zero, and a 0 would read as "every caller waited". The screen says "no calls yet today".
   */
  readonly answeredFirstTimePercent: number | null;
  readonly tier: TierView;
  readonly channelUnitInUse: number;
  /**
   * The organization's concurrency entitlement — what admission enforces across every workspace and agent (the tier's
   * ceiling, a plan's, a granted raise, channel packs; INT 2026-10-07). `channelUnitInUse` is read against it.
   */
  readonly organizationConcurrency: number;
}

export type Medium = "voice" | "web_chat" | "whatsapp" | "viber";

/** A conversation with `ended_at IS NULL` — the design's "live conversation". */
export interface LiveConversationView {
  readonly conversationId: string;
  /**
   * The far end, or `null` when this reader may not see it.
   *
   * Nullable because "Members never see caller details" is a DATABASE predicate (F-IDN-7):
   * `conversation_visible` NULLs `party_address` for a Member and a Viewer and still gives
   * them the row. A console type that could not express that would have to invent a string,
   * which is how a predicate gets quietly undone one layer above the database.
   */
  readonly partyAddress: string | null;
  readonly medium: Medium;
  /** `null` until the conversation has settled on one. */
  readonly languageCode: string | null;
  readonly phase: string;
  readonly elapsedSeconds: number;
  /**
   * WAPP-ENDCALL (2026-10-28): how an outbound conversation was started (`on_demand`, `test_call`, `campaign`), or
   * `null` (or absent) for an inbound one. "End call" is offered only where it is set — the conversations
   * `endOutboundConversation` accepts.
   */
  readonly outboundOrigin?: OutboundOrigin | null;
}

/**
 * plan-015 LIVE (G14, design §28.5.14): a row of ONE VoiceAgent's live board, with what its ⓘ shows beside the medium
 * and the id — the leg's observed codec and rate, and its latency so far. Each is `null` when not reported or not
 * measured yet; the console then says "Codec not reported" or omits the latency, never a placeholder. The codec is
 * the server's spelling (migration 0127's set), worded by the Live tab.
 */
export interface LiveBoardConversationView extends LiveConversationView {
  readonly mediaCodec: "pcmu" | "pcma" | "g722" | "l16" | "opus" | "amr_wb" | null;
  readonly mediaSampleRateHz: 8000 | 16000 | 24000 | 48000 | null;
  readonly latencyMs: number | null;
}

export interface ChannelUnitMeterView {
  readonly inUse: number;
  readonly ceiling: number;
  readonly note: string;
}

export interface LiveSnapshotView {
  readonly liveConversationCount: number;
  /** Whole seconds of talk today — seconds, not minutes, so nothing rounds before `talkTimeText`. */
  readonly talkSecondToday: number;
  readonly spendToday: Money;
  /** plan-017: today's conversations with billed seconds and no price, left out of `spendToday`. */
  readonly priceUnavailableCountToday: number;
  readonly outcomeCountToday: number;
  readonly outcomeLabel: string;
  readonly meter: ChannelUnitMeterView;
  readonly conversation: readonly LiveBoardConversationView[];
}

export interface TranscriptLineView {
  /** The transcript speaker label of the glossary (C1e): never bare "agent". */
  readonly speaker: "assistant" | "caller" | "system";
  readonly text: string;
  /** `true` while the recogniser is still revising this line (`turn.partial`). */
  readonly partial: boolean;
  /**
   * `turn.turn_index` where the source carries it (a stored row, a `turn.final` frame): how the
   * Live tab joins what was said before it opened to what it hears after, without a duplicate.
   */
  readonly turnIndex?: number;
  /**
   * How the line was produced — spoken or typed (plan-005 OP-44, `turn.mode`) — where the source
   * carries it (History's stored turns). What marks each line of a mixed conversation.
   */
  readonly mode?: "spoken" | "typed";
  /** When the line was said (RFC 3339), where the source carries it: places it among the switches. */
  readonly startedAt?: string;
}

/**
 * One part of a mixed widget conversation (plan-005 OP-44): a voice segment or a text stretch, with
 * the switch instant, its talk time and — for a voice segment — its recording where there is one.
 */
export interface ConversationSegmentView {
  readonly ordinal: number;
  readonly medium: "voice" | "web_chat";
  /** The switch (RFC 3339). */
  readonly startedAt: string;
  /** Whole seconds of talk in this part, or `null` while it is unanswered or open. */
  readonly talkSeconds: number | null;
  readonly recording: RecordingReferenceView | null;
}

/** A mixed conversation's seconds and charge per medium (OP-24's rule: the trial covers voice first). */
export interface MediumSplitView {
  readonly voiceSeconds: number;
  readonly textSeconds: number;
  readonly voiceChargedSeconds: number;
  readonly textChargedSeconds: number;
  readonly voiceAmount: Money;
  readonly textAmount: Money;
}

export interface ConversationEventView {
  readonly atText: string;
  readonly what: string;
}

/**
 * How a conversation came in (plan-004 WC-90, design §17.9.2): derived by the API from `medium`,
 * `is_test` and the binding's kind. `phone` a carrier leg; `browser_test` the console's voice test
 * from the browser; `website_voice` a visitor talking through the widget; `web_chat` a visitor
 * typing in it; `web_chat_with_voice` a visitor who typed and talked in one conversation (plan-005 D9,
 * "Web chat + voice"); `test_chat` the console's text test.
 */
export type ChannelLabel =
  | "phone"
  | "browser_test"
  | "website_voice"
  | "web_chat"
  | "web_chat_with_voice"
  | "test_chat"
  | "whatsapp"
  | "viber";

/** History's channel chip: every label, or one of them. */
export type HistoryChannelLabelFilter = "all" | ChannelLabel;

export interface PastConversationRowView {
  readonly conversationId: string;
  /** `null` for a Member and a Viewer — see {@link LiveConversationView.partyAddress}. */
  readonly partyAddress: string | null;
  readonly medium: Medium;
  readonly channelLabel: ChannelLabel;
  /** Six hex characters that tell website visitors apart (never the visitor key); `null` otherwise. */
  readonly visitorTag: string | null;
  /** Conversations sharing this one's party on the same binding, this one included. */
  readonly partyConversationCount: number;
  /** A word from the agent_template's outcome vocabulary, or `null` when none was reached. */
  readonly outcome: string | null;
  /**
   * Why it ended. `transferred` is an enumerated reason, which is what the row's tag reads —
   * matching on the outcome's wording would break for every template that words it
   * differently.
   */
  readonly endReason: string;
  readonly direction: "inbound" | "outbound";
  /** The language it ended in. `null` when it never settled on one. */
  readonly languageCode: string | null;
  readonly whenText: string;
  /** When it started (RFC 3339) — for a download's file name, never shown raw. */
  readonly startedAt: string;
  readonly billedSeconds: number;
  /** What billing charges for it: the seconds the free trial did not cover (review #109). */
  readonly billedAmount: Money;
  /** The seconds billing charges for — fewer than `billedSeconds` while the trial covers some. */
  readonly chargedSeconds: number;
  /** `billedSeconds` at the list rate, whoever pays for them. */
  readonly listAmount: Money;
  /** The customer-facing label of the version that answered, `v7`. */
  readonly agentVersionLabel: string | null;
  readonly isTest: boolean;
  /**
   * plan-017 (design §29.11 row 10): what it is priced as — its talk rate kind (a mixed website conversation: its voice
   * part's); `null` for a console test chat or a conversation from before per-kind rates.
   */
  readonly talkRateKind: TalkRateKind | null;
  /**
   * The rate it is priced at, a minute: its pinned rate of `talkRateKind` (0 for a test), or the rate a conversation
   * from before the cutover was priced at. `null` when it has no price.
   */
  readonly talkRatePerMinute: Money | null;
  /** It has billed seconds and no price: its money is left out, never guessed, and the row says so. */
  readonly priceUnavailable: boolean;
}

/**
 * Where a recording lives, as the API hands it over.
 *
 * A reference, never a playable URL: design §8.8 makes playing a recording an audited act
 * with its own route, and the audit entry precedes the URL so there is no window in which a
 * recording is reachable and unlogged.
 */
export interface RecordingReferenceView {
  readonly recordingId: string;
  readonly blobContainer: string;
  readonly blobName: string;
  readonly durationSeconds: number;
  readonly byteSize: number;
  readonly codec: string;
}

export interface PastConversationView extends PastConversationRowView {
  /**
   * The sentence on `transcript.summary`, or `null`.
   *
   * Nullable because a conversation has a transcript only once one has been generated, and
   * a panel that showed an empty paragraph would say "there is nothing to summarise" when
   * the truth is "this has not been summarised yet".
   */
  readonly summary: string | null;
  readonly transcript: readonly TranscriptLineView[];
  readonly event: readonly ConversationEventView[];
  /**
   * The recording, or `null` — and `null` means three things the console cannot tell apart,
   * deliberately: there is none, retention has deleted it, or this reader is a Member or a
   * Viewer and the DATABASE returned no row.
   */
  readonly recording: RecordingReferenceView | null;
  /**
   * A URL the browser can play, when one has been issued.
   *
   * Separate from {@link recording} because they are different facts: the reference says a
   * recording exists, the URL says this reader has been granted access to it through design
   * §8.8's audited path. Until that route exists, a live API answers with the reference and
   * no URL, and the panel says so rather than showing a player that cannot play.
   */
  readonly recordingUrl: string | null;
  readonly outcomeValue: Money | null;
  /**
   * `true` when this reader's role does not include transcripts — a Viewer reads analytics and
   * outcomes only (review #133). The transcript is then empty because it was withheld, not because
   * none was kept, and the panel says which.
   */
  readonly transcriptWithheld: boolean;
  /** The order this conversation placed, which the panel links to (review #113). */
  readonly customerOrder: ConversationCustomerOrderView | null;
  /**
   * The parts of a mixed conversation, in order (plan-005 OP-44); empty for a single-mode one. Absent
   * from a source that predates it (the sandbox), which reads as single-mode.
   */
  readonly segment?: readonly ConversationSegmentView[];
  /** A mixed conversation's seconds and charge per medium; `null` (or absent) for a single-mode one. */
  readonly mediumSplit?: MediumSplitView | null;
}

export interface ConversationCustomerOrderView {
  readonly reference: string;
  readonly total: Money;
  readonly status: string;
}

// ---------------------------------------------------------------------------------------
// Billing — P11-07
// ---------------------------------------------------------------------------------------

/**
 * The four states `creditStateOf` derives from §7.9's live balance.
 *
 * Note what is NOT here: `declined`. A decline is a fact about a card, not about the
 * balance — a funded account can have a declined auto-reload and keep answering — so the
 * banner reads it from {@link ChargeAttemptView} instead. Collapsing the two would make the
 * screen unable to say which one fired.
 */
export type CreditState = "trial" | "in_credit" | "exhausted" | "hard_stopped";

/**
 * The balance, in the three figures design §7.9 keeps apart.
 *
 * `settled` is what has been charged and paid. `available` is what may still be spent, after
 * today's unpriced seconds and the reservation every open conversation holds — and it is the
 * one the hard stop evaluates. A screen that showed only the first would tell a customer they
 * had credit while their agents had already stopped answering.
 */
export interface CreditBalanceView {
  readonly settled: Money;
  readonly unbilledToday: Money;
  readonly reserved: Money;
  readonly available: Money;
  /** The conversations in progress that hold `reserved` (2026-10-04). */
  readonly heldConversationCount: number;
  /** Whether the account has a trial grant at all (2026-10-04). */
  readonly hasTrialGrant: boolean;
  /** What a new conversation in this workspace would be told now, and why; `null`: admitted. */
  readonly refusal: BillingRefusalView | null;
  readonly trialSecondsRemaining: number;
  readonly talkRatePerMinute: Money;
  /**
   * The low-balance line in minutes of talk (`low_balance_talk_minute`, configured — owner answer 53, 60 at launch):
   * below it the shell's banner and the Billing screen call the balance low.
   */
  readonly lowBalanceTalkMinute: number;
  readonly hardStopEnabled: boolean;
  /** The BILLING ACCOUNT's state. A workspace at its budget is `workspaceBudget`, never this. */
  readonly state: CreditState;
  /** This workspace's monthly budget and its verdict; `null` when the workspace has none. */
  readonly workspaceBudget: WorkspaceBudgetView | null;
  /** Where invoices go (`billing_account.billing_email`) — the same for every member. */
  readonly billingEmail: string;
}

/**
 * A billing refusal as `GET /v1/billing/balance` words it (2026-10-04): the 402's code, why it fired
 * (`held_by_open_conversation` — a conversation in progress holds the credit and it frees up when that
 * conversation ends — or no credit, the trial used, the hard stop, the budget spent) and the sentence.
 */
export interface BillingRefusalView {
  readonly code: "credit_exhausted" | "trial_exhausted" | "hard_stop_reached" | "budget_exceeded";
  readonly cause: "held_by_open_conversation" | "credit_empty" | "trial_used" | "hard_stop" | "budget_spent";
  readonly held: Money;
  readonly message: string;
}

/** The live balance as {@link CreditFigures}, for `creditPartsOf` — the same figures both consoles print. */
export function creditFiguresOf(balance: CreditBalanceView): CreditFigures {
  return {
    availableMinor: balance.available.amountMinor,
    balanceMinor: balance.settled.amountMinor,
    unbilledTodayMinor: balance.unbilledToday.amountMinor,
    reservedMinor: balance.reserved.amountMinor,
    heldConversationCount: balance.heldConversationCount,
    trialSecondRemaining: balance.trialSecondsRemaining,
    hasTrialGrant: balance.hasTrialGrant,
    currency: balance.available.currency,
  };
}

/**
 * A workspace's monthly budget, judged exactly as admission judges it (design §7.9 (b), product
 * decision 2026-09-23): `reached` is the hard stop on and `spent` at or over `monthBudget`. When it
 * is, THIS workspace's agents stop answering while the account may still have credit.
 */
export interface WorkspaceBudgetView {
  readonly monthBudget: Money;
  readonly spent: Money;
  readonly hardStopEnabled: boolean;
  readonly reached: boolean;
}

/**
 * A stored card.
 *
 * `lastFour` is the only card data that exists anywhere in this platform. There is no PAN, no
 * expiry-plus-CVC pair and no cardholder input field, because the number is entered into
 * EveryPay's hosted Payform and WeTalk is handed a token (SAQ A).
 */
export interface PaymentMethodView {
  readonly paymentMethodId: string;
  readonly brand: string;
  readonly lastFour: string;
  readonly expiryMonth: number;
  readonly expiryYear: number;
  readonly holderName: string;
  readonly isDefault: boolean;
  /** Whether this card carries a mandate, which is what an auto-reload charges. */
  readonly hasMandate: boolean;
}

/**
 * What the processor's Payform is opened with, to add a card by buying credit (plan-002 RB-23,
 * design §15.6.3 as amended 2026-09-23).
 *
 * The console never renders a card field: EveryPay's own script (`scriptUrl`, loaded from the
 * processor and never bundled) mounts the form inside the page and hands back a single-use
 * token. `publicKey` is the processor's publishable key. `amount` is the credit pack's price as
 * the SERVER computed it — the form charges nothing itself, and the purchase that follows is
 * charged for the server's amount, not the form's.
 */
export interface HostedCardFormView {
  readonly creditPackCode: string;
  /**
   * AMENDED 2026-10-06 (FIX-MONEY C1): what the Payform opens with, 3-D Secure authenticates and the card is charged —
   * the pack's NET plus VAT, from the server's quote. Shown as "Total today" beside `net` and `tax`.
   */
  readonly amount: Money;
  /** The quote the form was opened for; the purchase confirms exactly this quote. */
  readonly billingQuoteId: string;
  /** The credit the balance receives (prices exclude VAT, OD-8). */
  readonly net: Money;
  /** The VAT on it, frozen for the organization's legal payer. */
  readonly tax: Money;
  readonly taxRatePermille: number;
  readonly publicKey: string;
  readonly scriptUrl: string;
  /** `recurring`: the first payment establishes the mandate automatic top-ups charge. */
  readonly frequencyType: "recurring";
}

export interface AutoReloadView {
  readonly configured: boolean;
  readonly enabled: boolean;
  /** `null` until the policy has been configured; never a zero standing in for absent. */
  readonly threshold: Money | null;
  readonly topUp: Money | null;
  readonly monthlyCap: Money | null;
  readonly monthCharged: Money | null;
  readonly paymentMethodId: string | null;
  /**
   * The policy's revision, sent back as `If-Match` when it is saved (plan-014 review I-9: the API refuses a save of
   * an existing policy without it, and a stale one `409 revision_stale`). `null` while nothing is configured.
   */
  readonly revision: number | null;
}

/** The workspace's spend controls. `hardStop` is the switch, never the verdict. */
export interface SpendControlView {
  readonly configured: boolean;
  readonly monthBudget: Money | null;
  readonly alertThresholdPercent: number | null;
  readonly hardStopEnabled: boolean;
  /**
   * plan-014 GAP: the spend cap's own behaviour (E3a) — a capped overage stops too, after its overage, so the hard-stop
   * switch alone cannot say it. `null` while nothing is configured.
   */
  readonly capBehavior: "hard_stop" | "capped_overage" | "notify_only" | null;
  /** A capped overage's allowance past the budget, in the budget's currency; `null` otherwise. */
  readonly overage: Money | null;
}

export type LedgerEntryKind = string;

/** One money row. `balanceAfter` is the running balance the database itself maintains. */
export interface LedgerEntryView {
  readonly ledgerEntryId: string;
  readonly sequence: number;
  readonly whenText: string;
  readonly kind: LedgerEntryKind;
  readonly description: string;
  readonly voiceAgentId: string | null;
  readonly usageDay: string | null;
  readonly billedSeconds: number | null;
  readonly amount: Money;
  readonly balanceAfter: Money;
}

export interface LedgerPageView {
  readonly row: readonly LedgerEntryView[];
  readonly nextCursor: string | null;
}

/** One `(voice_agent, day, medium, is_test)` grouping — the bars and the per-agent table. */
export interface UsageDayView {
  readonly voiceAgentId: string;
  readonly usageDay: string;
  readonly medium: Medium;
  readonly isTest: boolean;
  readonly billedSeconds: number;
  readonly conversationCount: number;
  /** The talk's list price, whoever paid for it. */
  readonly priced: Money;
  /** What billing charges for it: the free trial covers the seconds first (review #109). */
  readonly charged: Money;
  /** `false` for a grouping day close has not closed yet — today's, so far (review #103). */
  readonly closed: boolean;
  /** Whether the day is today in the workspace's zone. */
  readonly today: boolean;
}

export type ChargeAttemptState =
  | "created"
  | "sent"
  | "succeeded"
  | "declined"
  | "requires_action"
  | "requires_reauth"
  | "ambiguous";

export interface ChargeAttemptView {
  readonly chargeAttemptId: string;
  readonly kind: "cit" | "mit" | "refund";
  readonly amount: Money;
  readonly state: ChargeAttemptState;
  readonly declineCode: string | null;
  readonly declineMessage: string | null;
  readonly attemptCount: number;
  readonly whenText: string;
}

export interface InvoiceView {
  readonly invoiceId: string;
  readonly periodMonth: string;
  readonly minuteTotal: number;
  readonly amount: Money;
  /** The stored reference. Never a URL: issuing one is an access grant, not a listing. */
  readonly blobName: string | null;
}

export interface TopUpRequest {
  /** The NET credit bought (prices exclude VAT, OD-8). */
  readonly amount: Money;
  /** FIX-MONEY C1: the quote the card form was opened for, and the total (net + VAT) it opened with. */
  readonly billingQuoteId: string;
  readonly amountDue: Money;
  /** The token the hosted form produced. Never a card number; there is no field for one. */
  readonly cardToken: string;
  readonly saveCard: boolean;
}

export interface TopUpOutcomeView {
  readonly chargeAttemptId: string;
  readonly status: ChargeAttemptState | "error";
  readonly amount: Money;
  readonly balanceAfter: Money | null;
  /** Where to send the browser for a 3-D Secure challenge, when one is required. */
  readonly redirectUrl: string | null;
  readonly declineCode: string | null;
  readonly declineMessage: string | null;
  /**
   * True when the outcome is UNKNOWN and the reconciliation poller owns it. It means "go
   * look up what happened", never "try again": `merchant_ref` gives duplicate rejection, not
   * idempotency-key semantics.
   */
  readonly pendingReconciliation: boolean;
}

// ---------------------------------------------------------------------------------------
// Workspaces, people and the developer surface — P11-08
// ---------------------------------------------------------------------------------------

/**
 * Product decision Q10, closed: four roles, and Viewer is one of them.
 *
 * AMENDED 2026-10-05 (plan-014 A4): these are the roles the workspace PEOPLE routes offer and accept
 * today; the three presets of migration 0079 (Developer, Campaign manager, Billing) join them with
 * B3's workspace team. The auth_session and the organization picker name whichever of the SEVEN a
 * person holds ({@link WorkspaceRoleView}), so a Developer can sign in before the team page offers
 * the preset.
 */
//
// AMENDED 2026-10-06 (plan-014 B3, FR12-TEAM-05): the workspace people routes now offer and accept all SEVEN roles,
// so this is the seven-role set — kept as its own name because the people screens and their tests name it.
export type MemberRoleView = MemberRole;

/** Any of the seven workspace roles (design §27.5.1) — what a context and a workspace choice carry. */
export type WorkspaceRoleView = MemberRole;

export interface WorkspaceVoiceAgentView {
  readonly voiceAgentId: string;
  readonly displayName: string;
  readonly state: string;
  readonly agentTemplateName: string | null;
  /** Whole seconds of talk this month — shown through `talkTimeText`, never pre-rounded to minutes. */
  readonly monthTalkSecond: number;
  readonly monthSpend: Money;
  /** When it was made, "23 Sep 2026" — tells two VoiceAgents with the same name apart (review #161). */
  readonly createdAtText: string;
}

export interface WorkspaceDetailView {
  readonly workspaceId: string;
  readonly name: string;
  readonly initials: string;
  readonly slug: string;
  readonly timeZone: string;
  readonly state: "active" | "archived";
  readonly memberCount: number;
  readonly voiceAgentCount: number;
  /** Whole seconds of talk this month — shown through `talkTimeText`, never pre-rounded to minutes. */
  readonly monthTalkSecond: number;
  readonly monthSpend: Money;
  readonly createdAtText: string;
  readonly voiceAgent: readonly WorkspaceVoiceAgentView[];
}

/**
 * One person in a workspace.
 *
 * `displayName` and `emailAddress` are nullable because `membership` is tenant-scoped and
 * `person` is not: there is no role that can join them, so the name comes from a second read
 * and a membership can outlive the person row it points at.
 */
export interface WorkspaceMemberView {
  readonly membershipId: string;
  readonly personId: string;
  readonly displayName: string | null;
  readonly emailAddress: string | null;
  readonly emailVerified: boolean;
  readonly role: MemberRoleView;
  readonly joinedAtText: string | null;
  readonly lastActiveText: string | null;
}

export interface WorkspaceInvitationView {
  readonly invitationId: string;
  readonly emailAddress: string;
  readonly role: MemberRoleView;
  readonly expiresAtText: string;
  /** plan-014 B3 (FR12-TEAM-05): pending, expired or revoked — shown apart from the members. */
  readonly state: WorkspaceInvitationState;
}

/** The states the People tab shows an invitation in (accepted ones are members). */
export type WorkspaceInvitationState = "pending" | "expired" | "revoked";

export interface WorkspacePeopleView {
  readonly member: readonly WorkspaceMemberView[];
  readonly invitation: readonly WorkspaceInvitationView[];
}

/** An invitation at the one moment its token exists. Shown once; never recoverable. */
export interface IssuedInvitationView extends WorkspaceInvitationView {
  readonly token: string;
}

export interface ApiKeyView {
  readonly apiKeyId: string;
  readonly name: string;
  /**
   * The fixed public prefix every key carries — `wtk_live_`.
   *
   * It is NOT a per-key discriminator and there is none: `api_key` stores this and a digest,
   * so the design's `wtk_live_····3f2a` cannot be rendered and must not be, because any such
   * tail would be a slice of the secret. The key's `name` is what tells two keys apart.
   */
  readonly keyPrefix: string;
  readonly scope: readonly string[];
  readonly rateLimitPerMinute: number;
  readonly lastUsedText: string | null;
  /** When the key was made, worded like `lastUsedText` (customer-portal review item 192). */
  readonly createdText: string;
  /** Who made it; `null` when they are no longer in the account (review item 192). */
  readonly createdByName: string | null;
  readonly revoked: boolean;
}

/** A key at the one moment its value exists. There is no route that can produce it again. */
export interface IssuedApiKeyView extends ApiKeyView {
  readonly key: string;
}

export interface ApiKeyDraft {
  readonly name: string;
  readonly scope: readonly string[];
}

/**
 * A webhook destination, as Settings renders it — and four fields, not five.
 *
 * **There is no secret here and there cannot be.** `webhook_endpoint.signing_secret_ref` is a
 * Key Vault *name*, not a value (ADR 0011): WeTalk stores where the secret lives and never
 * the secret, so the design's "Reveal" button has nothing to reveal. `GET
 * /v1/webhook-endpoints` does not project the reference columns either, so even the
 * *configured / rotation pending* state is not on this wire — and inventing `true` for it
 * would be a claim about Key Vault made by a screen that has never talked to Key Vault.
 *
 * Rotation is design §5.2's `POST /v1/webhook-endpoints/{id}/rotate-secret`, which P6-02
 * deliberately did not build (writing a new secret and recording its name needs the
 * environment slug and a vault client). Settings says so in words instead of offering a
 * control that cannot work.
 */
export interface WebhookEndpointView {
  readonly webhookEndpointId: string;
  readonly url: string;
  readonly eventKind: readonly string[];
  readonly enabled: boolean;
}

// ---------------------------------------------------------------------------------------
// The Test console — P11-09
// ---------------------------------------------------------------------------------------

export type TestCallDirection = "agent_calls_me" | "i_call_the_agent";

export interface TestCallRequest {
  readonly voiceAgentId: string;
  readonly direction: TestCallDirection;
  /** The tester's own number. Empty when the tester is dialling in. */
  readonly partyNumber: string;
  readonly languageCode: string;
}

/**
 * `connecting` (plan-004 WC-51): a browser test the api has armed, or the browser has joined, that
 * admission has not carried yet — nothing is billed while it lasts.
 */
export type TestCallState = "idle" | "dialing" | "connecting" | "live" | "ended";

/**
 * A voice test call.
 *
 * It is a **real conversation on the live VoiceAgent**, over the real carrier connection, and
 * it is billed like any other minute — `conversation.is_test` is what marks it, not a
 * separate free path. That is why this view carries `billedSeconds` and `billedAmount` at
 * all: a sandbox that could not show the cost would be hiding the one thing the disclosure
 * promises.
 */
export interface TestCallView {
  readonly conversationId: string | null;
  readonly state: TestCallState;
  readonly elapsedSeconds: number;
  readonly billedSeconds: number;
  /** `billedSeconds` at the list rate, whoever pays for them. */
  readonly billedAmount: Money;
  /**
   * What billing charges once the call has ended — fewer than `billedSeconds` while the free trial
   * covers some, zero when it covered all (review #233). `null` until it ends.
   */
  readonly chargedSeconds: number | null;
  /** `chargedSeconds` at the account's rate; `null` until it ends. */
  readonly chargedAmount: Money | null;
  readonly transcript: readonly TranscriptLineView[];
  /** Why it ended, when it has. `null` while it is running. */
  readonly endReason: string | null;
  /**
   * "I call the agent" (customer-portal review #125): the agent's number to dial, and the window the
   * platform waits for the tester's call in. `null` on every other answer.
   */
  readonly dialNumber: string | null;
  readonly waitingSince: string | null;
  readonly waitingUntil: string | null;
}

/**
 * One line of the text sandbox. Text chat is free; nothing is metered. `system` says what the agent
 * would have carried out — a text test carries nothing out (review #91).
 */
export interface TestChatTurnView {
  readonly speaker: "assistant" | "caller" | "system";
  readonly text: string;
}

/** What can be tested right now, read when a test surface opens (review #166). */
export interface TestCallAvailabilityView {
  readonly modelRunning: boolean;
  readonly sandboxTurnAvailable: boolean;
  readonly directionAvailable: readonly TestCallDirection[];
  /** Why something cannot run, in the API's words; `null` when everything can. */
  readonly message: string | null;
  /**
   * Whether "Talk in the browser" can start now (plan-004 WC-51, FR-BV-3): the runtime answered, the
   * model is running and WeTalk's browser voice relay is reachable.
   */
  readonly browserTestCallAvailable: boolean;
  /** Why a browser test cannot start, in the API's words; `null` when it can. */
  readonly browserTestCallMessage: string | null;
  /** The account's calling code (`+30`), for the phone field's example (review #56). */
  readonly countryCallingCode: string | null;
}

/** The History tab's typed search state — mirrored one-for-one into the URL. */
export type HistoryRange = "today" | "yesterday" | "7d" | "30d" | "90d" | "all";

/**
 * The History tab's outcome chip (review #113, #122): one of the four fixed filters, or one
 * outcome exactly as the agent_template words it ("Order placed"), which the page's
 * `outcomeOption` lists. `none` is every conversation that reached no outcome — named for what it
 * filters, where "Question only" was not.
 */
export type HistoryOutcomeFilter = "all" | "reached" | "transferred" | "none" | (string & {});

/** The four chips that are not an outcome's own word. */
export const HISTORY_OUTCOME_KEYWORD: readonly string[] = ["all", "reached", "transferred", "none"];

export interface HistoryQuery {
  readonly voiceAgentId: string;
  readonly range: HistoryRange;
  readonly outcome: HistoryOutcomeFilter;
  readonly search: string;
  /** The channel chip (plan-004 WC-90). */
  readonly channelLabel: HistoryChannelLabelFilter;
  /** One party's conversations, named by one of them; `""` is no such filter. */
  readonly partyOf: string;
  /**
   * One number's conversations, named by its `phone_number_id` (numbers-sip-sync, "See all calls on this number");
   * absent or `""` is no such filter. Optional so every History link that does not name a number stays as it was.
   */
  readonly phoneNumberId?: string;
}

export interface HistoryStatView {
  readonly label: string;
  readonly value: string;
  /** What the figure counts, where the design prints nothing: the tile's `title`. */
  readonly note: string | null;
}

export interface PastConversationPageView {
  readonly row: readonly PastConversationRowView[];
  readonly stat: readonly HistoryStatView[];
  /** The outcomes this VoiceAgent's conversations have reached, most frequent first. */
  readonly outcomeOption: readonly string[];
  /** The channel labels this VoiceAgent's conversations carry, most frequent first. */
  readonly channelLabelOption: readonly ChannelLabel[];
}

// ---------------------------------------------------------------------------------------
// Agent · Channels, Numbers & SIP, Messaging (P11-04 / P11-05 / P11-06)
//
// THREE OF THESE TYPES EXIST BECAUSE THE CARRIER CANNOT DO WHAT THE DESIGN DREW.
//
//   `sipAddressOwner`      whose domain the SIP address is on. `carrier` today: Telnyx's
//                          vanity SIP domains exist only on IP/FQDN/Teams-auth connections,
//                          never on the credential connections a per-VoiceAgent user-part
//                          needs, so `sip:<slug>@sip.wetalk.io` is not deliverable (A7).
//   `InboundFailoverView`  an object with a reason and a named alternative, never a boolean.
//                          A boolean invites a badge; a platform number is homed on one
//                          carrier and moving it is a porting operation measured in days (A8).
//   `RegionLatencyView`    `measured` is a field because "region · latency" is NOT a vendor
//                          field. It is a WeTalk probe landing in `region_latency_sample`,
//                          and when nothing has probed the console says so.
// ---------------------------------------------------------------------------------------

/** The Channel detail's three states, as the design names them. */
export type MediumState = "connected" | "coming_soon" | "not_connected";

export interface ChannelBindingRowView {
  readonly channelBindingId: string;
  readonly kind: string;
  readonly medium: Medium;
  readonly address: string;
  readonly carrier: string | null;
  readonly enabled: boolean;
  readonly conversationLast7Day: number;
  readonly lastConversationAtText: string | null;
}

export interface ConcurrencySummaryView {
  readonly inUse: number;
  /** `null` when nothing is published — not the same as a ceiling of zero. */
  readonly channelUnitCeiling: number | null;
  readonly voiceAgentState: string;
  /** plan-014 GAP: the ORGANIZATION's concurrency entitlement (E4's reader), never the tier's figure. */
  readonly organizationChannelUnit: number;
}

/** Why inbound cannot fail over, and what can be done instead. Never a boolean. */
export interface InboundFailoverView {
  readonly possible: false;
  readonly reason: string;
  readonly explanation: string;
  readonly alternative: string;
}

export interface SipConnectionView {
  readonly sipCredentialId: string;
  readonly username: string;
  readonly connectionHost: string;
  readonly sipAddress: string;
  /** `carrier` today. The console must never present a carrier hostname as WeTalk's. */
  readonly sipAddressOwner: "wetalk" | "carrier";
  readonly codec: readonly string[];
  /** A Key Vault secret NAME. There is no password field, here or on the wire. */
  readonly secretRef: string;
  readonly rotatedAtText: string | null;
}

export interface RegistrationView {
  readonly observedAtText: string;
  readonly responseCode: number | null;
  readonly synthesised: boolean;
}

export interface ConnectionNumberView {
  readonly e164: string;
  readonly country: string;
  /** `null` for a number on the account's own SIP trunk (`binding_kind` `sip_trunk_number`). */
  readonly carrier: string | null;
  readonly source: string;
  readonly bindingKind: string;
}

export interface VoiceAgentConnectionView {
  readonly slug: string;
  readonly voiceAgentState: string;
  readonly number: ConnectionNumberView | null;
  readonly sip: SipConnectionView | null;
  /**
   * `null` until the hooks hostname is delegated (RC-73, review #94): a URL that does not resolve
   * is not shown.
   */
  readonly eventsUrl: string | null;
  /**
   * What WeTalk itself has seen on the voice line (review #94) — the status of a carrier number
   * comes from here, since a carrier number never REGISTERs. `null` from an API that predates it.
   */
  readonly voiceObserved: VoiceObservedView | null;
  /** `null` means nothing has been observed — which is not the same as "not registered". */
  readonly registration: RegistrationView | null;
  readonly inboundFailover: InboundFailoverView;
}

/** The voice line as WeTalk observed it: the latest non-test conversation and the week's count. */
export interface VoiceObservedView {
  /** "2 hours ago", or `null` when no conversation has arrived yet. */
  readonly lastConversationAtText: string | null;
  readonly conversationLast7Day: number;
}

export interface OpeningIntervalView {
  readonly opensAt: string;
  readonly closesAt: string;
}

export interface OpeningDayView {
  readonly day: string;
  readonly label: string;
  /** Empty means closed. A stated fact, never a missing key. */
  readonly interval: readonly OpeningIntervalView[];
}

export interface ClosedDateView {
  readonly date: string;
  readonly label: string;
  readonly interval: readonly OpeningIntervalView[];
}

export interface AnsweringBehaviourView {
  readonly kind: string;
  readonly phraseKey: string | null;
  readonly maxWaitMs: number | null;
  readonly transferTo: string | null;
}

export interface OverflowTallyView {
  readonly behaviour: string;
  readonly occurrence: number;
  readonly longestWaitMs: number;
}

export interface AnsweringScheduleView {
  readonly published: boolean;
  readonly timeZone: string;
  /** The zone the live agent_version was built against. `null` when nothing is published. */
  readonly configuredTimeZone: string | null;
  readonly week: readonly OpeningDayView[];
  readonly closedDate: readonly ClosedDateView[];
  readonly afterHours: AnsweringBehaviourView | null;
  readonly overflow: AnsweringBehaviourView | null;
  readonly overflowLast7Day: readonly OverflowTallyView[];
}

export interface SignallingEventView {
  readonly signallingEventId: string;
  readonly atText: string;
  readonly kind: string;
  readonly peer: string | null;
  readonly responseCode: number | null;
  readonly detail: string | null;
  /** WeTalk manufactured this fact because the carrier does not expose it. */
  readonly synthesised: boolean;
}

export interface SipRotationView {
  readonly sipCredentialId: string;
  readonly state: string;
  /** Unchanged: the user-part does not move, so nothing needs re-pointing. */
  readonly sipAddress: string;
}

export interface NumberRowView {
  readonly phoneNumberId: string;
  readonly e164: string;
  readonly country: string;
  /**
   * The carrier the number is homed on, or `null` for a number on the account's own SIP trunk
   * (plan-008: `provider: sip_trunk`, and the table's CHECK makes it one or the other). The trunk's
   * own facts — its name, the wire form, the caller-ID switch — are read from `listSipTrunks`.
   */
  readonly carrier: string | null;
  readonly source: string;
  readonly monthlyCost: Money;
  readonly purchasedAtText: string;
  readonly releasedAtText: string | null;
  /** `2 Jun 2026` — the day the workspace took the number (numbers-sip-sync: "Since", "In use"). */
  readonly purchasedOn: string;
  /** `18 Aug 2026` — the day it was released; `null` while the workspace holds it. */
  readonly releasedOn: string | null;
  /** How a released number left, from what the rows hold (`release_cause`); `null` while held. */
  readonly releaseCause: NumberReleaseCause | null;
  /** The trunk a trunk number is on (plan-008), its name and whether it presents as caller ID; null / false for a carrier number. */
  readonly sipTrunkId: string | null;
  readonly sipTrunkDisplayName: string | null;
  readonly isCallerId: boolean;
  readonly channelBindingId: string | null;
  readonly bindingKind: string | null;
  readonly enabled: boolean | null;
  readonly voiceAgentId: string | null;
  readonly voiceAgentName: string | null;
  readonly inboundFailover: boolean;
}

/** `listNumbers`' `release_cause` (numbers-sip-sync). */
export const NUMBER_RELEASE_CAUSE = ["sip_trunk_deleted", "removed_from_sip_trunk", "released_by_wetalk", "released"] as const;
export type NumberReleaseCause = (typeof NUMBER_RELEASE_CAUSE)[number];

/** One conversation of a number's "Recent activity" (`getNumberActivity`, numbers-sip-sync). */
export interface NumberActivityConversationView {
  readonly conversationId: string;
  readonly voiceAgentId: string;
  readonly voiceAgentName: string | null;
  readonly direction: "inbound" | "outbound";
  readonly isTest: boolean;
  /** The far end; `null` for a Member or a Viewer (the caller-detail predicate). */
  readonly partyAddress: string | null;
  /** `Today 14:02` — when it started, in the browser's zone. */
  readonly startedAtText: string;
  readonly answered: boolean;
  readonly billedSecond: number | null;
  readonly outcome: string | null;
  readonly endReason: string;
}

/** A number's "Last call", "This month" and "Recent activity" (`GET /v1/numbers/{phone_number_id}/activity`). */
export interface NumberActivityView {
  readonly phoneNumberId: string;
  /** `Today 14:02`; `null` when no conversation has been on the number. */
  readonly lastConversationAtText: string | null;
  readonly monthConversationCount: number;
  readonly conversation: readonly NumberActivityConversationView[];
}

export interface NumberPageView {
  readonly row: readonly NumberRowView[];
  readonly sipTrunkCount: number;
}

export interface WorkspaceSipCredentialView extends SipConnectionView {
  readonly voiceAgentId: string;
  readonly voiceAgentName: string;
}

export interface RegionLatencySampleView {
  readonly carrier: string;
  readonly region: string;
  readonly probeKind: string;
  readonly rttMs: number;
  readonly measuredAtText: string;
}

export interface RegionLatencyView {
  /** False when nothing has probed inside the window. The console renders the absence. */
  readonly measured: boolean;
  readonly source: string;
  readonly windowHours: number;
  readonly detail: string;
  readonly sample: readonly RegionLatencySampleView[];
}

export interface MessagingMediumRowView {
  readonly medium: Medium;
  readonly name: string;
  readonly dek: string;
  readonly detail: string;
  readonly availability: "open" | "waitlisted";
  readonly waitlistReason: string | null;
  readonly state: MediumState;
  readonly bindingCount: number;
  readonly enabledBindingCount: number;
  readonly conversationLast30Day: number;
  readonly liveConversationCount: number;
  readonly lastConversationAtText: string | null;
}

export interface MessagingBindingView {
  readonly channelBindingId: string;
  readonly kind: string;
  readonly address: string;
  readonly enabled: boolean;
  readonly carrier: string | null;
  readonly voiceAgentId: string;
  readonly voiceAgentName: string;
}

export interface MediumConnectStepView {
  readonly title: string;
  readonly dek: string;
  readonly actionLabel: string | null;
}

export interface MediumConnectFieldView {
  readonly code: string;
  readonly label: string;
  readonly placeholder: string;
}

export interface AnswerableVoiceAgentView {
  readonly voiceAgentId: string;
  readonly name: string;
}

/**
 * The web-chat widget.
 *
 * `scriptTag` is composed by the gateway from the console's own configured API origin, never
 * from a hostname the API invented: WeTalk has no decided CDN, and putting an unprovisioned
 * one into a customer's page source is the same mistake as calling `sip.telnyx.com` ours.
 * The key is a `channel_binding_id` — public by construction, and not a secret.
 */
export interface WebChatWidgetView {
  readonly widgetKey: string;
  readonly scriptTag: string;
}

export interface MessagingMediumDetailView extends MessagingMediumRowView {
  readonly blurb: string;
  readonly binding: readonly MessagingBindingView[];
  readonly billedSecondLast30Day: number;
  readonly step: readonly MediumConnectStepView[];
  readonly field: readonly MediumConnectFieldView[];
  readonly answerableVoiceAgent: readonly AnswerableVoiceAgentView[];
  readonly widget: WebChatWidgetView | null;
}

/**
 * One conversation on one of the workspace's website widgets, typed or spoken (plan-004 WC-90,
 * design §17.10.7) — a row of the Messaging page's conversation table.
 */
export interface WebChatConversationRowView {
  readonly conversationId: string;
  /** The VoiceAgent that answered: the row links to its History. */
  readonly voiceAgentId: string;
  readonly voiceAgentName: string;
  readonly medium: Medium;
  readonly channelLabel: ChannelLabel;
  /** `null` for a Member and a Viewer, by the database predicate. Never printed for a visitor. */
  readonly partyAddress: string | null;
  readonly visitorTag: string | null;
  /** This visitor's conversations on the same widget, this one included. */
  readonly partyConversationCount: number;
  /** The last line said, or `null` (none yet, or withheld from this reader's role). */
  readonly lastMessage: string | null;
  readonly transcriptWithheld: boolean;
  readonly languageCode: string | null;
  readonly outcome: string | null;
  readonly endReason: string;
  readonly whenText: string;
  readonly billedSeconds: number | null;
  readonly isTest: boolean;
}

// ---------------------------------------------------------------------------------------
// The seam
// ---------------------------------------------------------------------------------------

// ---------------------------------------------------------------------------------------
// Agent · Analytics, Versions and Review (P11-01, P11-02, P11-03)
// ---------------------------------------------------------------------------------------

/** The Analytics tab's range control, mirrored into the URL. */
export type AnalyticsRange = "24h" | "7d" | "14d" | "30d" | "90d" | "all";

export interface AnalyticsDailyPointView {
  /** `YYYY-MM-DD` in the workspace's zone. */
  readonly day: string;
  readonly conversationCount: number;
}

export interface AnalyticsSliceView {
  /** The outcome, the language or the channel, already worded for the screen. */
  readonly label: string;
  readonly conversationCount: number;
}

export interface AnalyticsMediumSliceView extends AnalyticsSliceView {
  readonly medium: Medium;
  readonly direction: "inbound" | "outbound";
  /** What tells website voice from a phone call, both `voice` (plan-004 WC-90). */
  readonly channelLabel: ChannelLabel;
}

export interface AnalyticsHourView {
  readonly hour: number;
  readonly conversationCount: number;
}

export interface AgentVersionComparisonView {
  readonly agentVersionId: string;
  readonly label: string;
  readonly isLive: boolean;
  readonly conversationCount: number;
  readonly outcomeReachedCount: number;
  /** `null` when nothing was answered on that version in the window. */
  readonly averageSeconds: number | null;
}

export interface RankedGapView {
  readonly gapId: string;
  readonly question: string;
  readonly callerCount: number;
}

export interface VoiceAgentAnalyticsView {
  /** The zone every day and hour bucket below is cut in. */
  readonly timeZone: string;
  /** The template's own word for what it counts — the label above the success figure. */
  readonly successMetricLabel: string;
  readonly conversationCount: number;
  /** `null` when nothing was answered. An average of nothing is not zero. */
  readonly averageSeconds: number | null;
  /** The window's whole seconds of talk — shown through `talkTimeText`. */
  readonly talkSecond: number;
  /** What billing charges for the window: trial seconds first (review #109). */
  readonly spend: Money;
  /** The window's seconds at the list rate, whoever pays for them. */
  readonly listSpend: Money;
  /** `true` when the free trial covered some of the window's seconds. */
  readonly trialCovered: boolean;
  readonly outcomeReachedCount: number;
  /** Review 120: History lists these and the figures leave them out — answered test calls. */
  readonly excludedTestCallCount: number;
  /** Review 120: calls in the window that were never answered (test calls aside). */
  readonly excludedUnansweredCount: number;
  /** plan-017: conversations with billed seconds and no price, left out of `spend`. */
  readonly priceUnavailableCount: number;
  readonly daily: readonly AnalyticsDailyPointView[];
  readonly outcome: readonly AnalyticsSliceView[];
  readonly language: readonly AnalyticsSliceView[];
  readonly byMedium: readonly AnalyticsMediumSliceView[];
  readonly hour: readonly AnalyticsHourView[];
  readonly agentVersion: readonly AgentVersionComparisonView[];
  readonly gap: readonly RankedGapView[];
}

/** What changed between one agent_version and the one before it. */
export interface AgentVersionChangeView {
  readonly kind: "added" | "removed" | "changed";
  readonly subject: string;
  readonly detail: string;
}

export interface AgentVersionSummaryView {
  readonly agentVersionId: string;
  readonly label: string;
  readonly ordinal: number;
  readonly state: "building" | "ready" | "failed" | "archived";
  readonly note: string | null;
  readonly change: readonly AgentVersionChangeView[];
  readonly languageCode: readonly string[];
  readonly greeting: string;
  readonly channelUnitCeiling: number;
  /** `null` while the version has never been published. */
  readonly publishedAtText: string | null;
  readonly createdAtText: string;
  /** Who made it, as a population: a member, a WeTalk operator, or an automatic process. */
  readonly authoredBy: "member" | "operator" | "unknown";
  /**
   * The author's display name (customer-portal review #95), read by the API through
   * `person_in_account`; `null` for an operator or an automatic process. The screen used to say
   * "someone on your team" because only the id was on the wire.
   */
  readonly authorName: string | null;
  /**
   * Who FIRST published it: a person (named), a WeTalk operator (named when acting as a member),
   * or `system` — the first build publishing itself (decision D14 (a)). `null` while never published.
   */
  readonly publishedBy: {
    readonly kind: "person" | "operator" | "system";
    readonly name: string | null;
  } | null;
  readonly isLive: boolean;
}

export interface TestCaseResultView {
  readonly testCaseId: string;
  readonly utterance: string;
  readonly expectation: string;
  /** `null` when this case was not in the most recent run. */
  readonly passed: boolean | null;
  readonly resultText: string | null;
  readonly transcriptExcerpt: string | null;
}

export interface TestSuiteView {
  readonly testSuiteId: string;
  readonly name: string;
  readonly description: string | null;
  readonly testCase: readonly TestCaseResultView[];
  /** `null` when the suite has never been run. */
  readonly lastRunAgainstLabel: string | null;
  readonly lastRunAtText: string | null;
  readonly passedCount: number;
  readonly failedCount: number;
}

export type GapState = "open" | "queued" | "folded" | "dismissed";

export interface GapView {
  readonly gapId: string;
  readonly question: string;
  readonly representativeQuote: string | null;
  readonly callerCount: number;
  readonly lastSeenText: string;
  readonly answerText: string | null;
  readonly answeredIntoLabel: string | null;
  readonly state: GapState;
  /** The latest conversation that asked it — "Open the call" (review #93). */
  readonly lastConversationId: string | null;
}

/** What `POST /v1/agents/{voice_agent_id}/gaps/publish` answers (2026-09-30). */
export interface PublishedAnswersView {
  readonly liveVersionSeq: number;
  /** How many queued answers this publish folded into the new version. */
  readonly publishedGapCount: number;
  /** How many answers the new version carries, earlier ones included. */
  readonly answerCount: number;
}

/** Which population raised the concern. `conversation_flag.raised_by_kind`, verbatim. */
export type ConversationFlagRaisedBy = "member" | "operator" | "automatic" | "carrier";

export interface ConversationFlagView {
  readonly conversationFlagId: string;
  readonly conversationId: string;
  readonly severity: "urgent" | "review";
  readonly reasonCode: string;
  /** The quoted words that prompted it, or `null` when none was captured. */
  readonly excerpt: string | null;
  readonly raisedByKind: ConversationFlagRaisedBy;
  readonly raisedAtText: string;
  readonly handled: boolean;
  /** `null` for a Member and a Viewer — the database withholds it, not this console. */
  readonly partyAddress: string | null;
  readonly medium: Medium;
  readonly billedSeconds: number | null;
}

export interface ConsoleGateway extends OrderBookGateway, BiConnectionGateway {
  // --- identity (P5-01, P5-05) ----------------------------------------------------------
  /**
   * `guard`: the entry card's trap value and solved identity challenge (ADR 0071). Without one the api gateway solves a
   * fresh challenge itself (`./identity-challenge.ts`), as it does for `logIn` and `signUp`, so no caller can forget it.
   */
  discoverIdentifier(emailAddress: string, guard?: EntryGuardView): Promise<DiscoveryOutcome>;
  /** `GET /v1/auth/challenge` (ADR 0071): the body, for `challengeOf` — solved by the entry card before each step. */
  readIdentityChallenge(): Promise<unknown>;
  // `signUp` — declared in `./slice/account.ts` (RB-17's slice), because RB-17 changes it.
  /**
   * Raises {@link AuthSessionUnscopedError} for a person with no account, or several to choose
   * from; a person with exactly one is opened in it (design §27.6.3) — in an account-only context
   * when they hold only an organization role there.
   */
  logIn(request: LogInRequestView): Promise<AuthSessionView>;
  /**
   * `null` when nobody is signed in; otherwise the context, in whichever of its three states it is
   * (plan-014 A4: an unscoped sign-in is no longer an error here — the shell offers the picker).
   */
  currentAuthSession(): Promise<AuthSessionView | null>;
  /**
   * Switches organization and/or workspace (`POST /v1/session/account` with the current scope
   * generation, plan-014 A2): `workspaceId: null` opens the organization alone. Rotates the
   * auth_session; the new CSRF token and scope generation are learned. A stale generation is
   * `409 scope_changed`; a stricter destination is `401 assurance_required`.
   */
  switchScope(target: ScopeTargetView): Promise<SwitchedScopeView>;
  /** The scope generation this gateway sends, and the scope guard watches (`../scope/generation.ts`). */
  readonly scopeGeneration: ScopeGeneration;
  /** Ends the auth_session. Resolves when it is ended, including when it already was. */
  logOut(): Promise<void>;
  /** Where "Continue with Google" sends the browser. A URL, not a fetch. */
  googleStartUrl(returnPath: string): string;
  /**
   * The public API reference on `apps/web-public` (`/docs/api/`): every operation, the scope each
   * needs, which need a signed-in person, and a key's limits. Settings → Developers' "Read the API
   * docs" and the Documentation list open it (customer-portal review item 188). A URL, not a fetch.
   */
  apiReferenceUrl(): string;
  /**
   * A page of the public site (`apps/web-public`), built from the configured public-site base — the sign-in pages'
   * header and footer link there (ADR 0056, portal amendment). `path` starts with `/` and may carry a fragment. A URL, not a fetch.
   */
  publicSiteUrl(path: string): string;

  // --- shell -----------------------------------------------------------------------------
  listWorkspaces(): Promise<readonly WorkspaceView[]>;
  getBillingState(): Promise<BillingStateView>;
  // `listNotifications`, `readAllNotifications` — `./slice/notification.ts` (RB-21).
  // `getFirstRunProgress` — `./slice/workspace-home.ts` (RB-22).

  // --- catalogue (documented) ------------------------------------------------------------
  listAgentTemplates(): Promise<readonly AgentTemplateCardView[]>;
  /**
   * plan-015 INT-B (design §28.12.4): whether a call may be forwarded to a phone, and the facts the forward copy
   * states — what `GET /v1/templates` answers beside its templates, so the New agent wizard knows before any agent
   * exists and forwarding switched on needs no console deploy.
   */
  readForwardFact(): Promise<ForwardFact>;
  listLanguagePacks(): Promise<readonly LanguagePackView[]>;
  listVoices(): Promise<readonly VoiceView[]>;
  listTiers(): Promise<readonly TierView[]>;
  getNumberAvailability(): Promise<NumberAvailabilityView>;

  // --- the wizard -------------------------------------------------------------------------
  createVoiceAgent(draft: VoiceAgentDraft): Promise<CreatedVoiceAgentView>;
  listVoiceAgents(): Promise<readonly VoiceAgentView[]>;
  getVoiceAgent(voiceAgentId: string): Promise<VoiceAgentView>;

  // --- the agent surface --------------------------------------------------------------------
  // `getDashboardFigures` — `./slice/workspace-home.ts` (RB-22).
  getLiveSnapshot(voiceAgentId: string): Promise<LiveSnapshotView>;
  listPastConversations(query: HistoryQuery): Promise<PastConversationPageView>;
  getPastConversation(conversationId: string): Promise<PastConversationView>;

  // --- billing (P11-07) --------------------------------------------------------------------
  getCreditBalance(): Promise<CreditBalanceView>;
  listLedgerEntries(cursor: string | null): Promise<LedgerPageView>;
  listPaymentMethods(): Promise<readonly PaymentMethodView[]>;
  setDefaultPaymentMethod(paymentMethodId: string): Promise<void>;
  removePaymentMethod(paymentMethodId: string): Promise<void>;
  // `startHostedCardForm` and `completeCardPurchase` — `./slice/billing-card.ts` (RB-23).
  getAutoReload(): Promise<AutoReloadView>;
  saveAutoReload(policy: {
    /** The revision the screen read (`null`: no policy yet). */
    readonly revision: number | null;
    readonly enabled: boolean;
    readonly threshold: Money;
    readonly topUp: Money;
    readonly monthlyCap: Money;
    readonly paymentMethodId: string;
  }): Promise<AutoReloadView>;
  getSpendControl(): Promise<SpendControlView>;
  saveSpendControl(control: {
    readonly monthBudget: Money;
    readonly alertThresholdPercent: number;
    readonly hardStopEnabled: boolean;
  }): Promise<SpendControlView>;
  listUsageDays(fromDay: string, toDay: string): Promise<readonly UsageDayView[]>;
  listChargeAttempts(): Promise<readonly ChargeAttemptView[]>;
  listInvoices(): Promise<readonly InvoiceView[]>;
  buyCredit(request: TopUpRequest): Promise<TopUpOutcomeView>;

  // --- workspaces, people and the developer surface (P11-08) --------------------------------
  createWorkspace(draft: { readonly name: string; readonly timeZone: string }): Promise<WorkspaceView>;
  getWorkspace(workspaceId: string): Promise<WorkspaceDetailView>;
  renameWorkspace(
    workspaceId: string,
    draft: { readonly name: string; readonly timeZone: string },
  ): Promise<WorkspaceDetailView>;
  archiveWorkspace(workspaceId: string): Promise<void>;
  listWorkspacePeople(workspaceId: string): Promise<WorkspacePeopleView>;
  inviteToWorkspace(
    workspaceId: string,
    invitation: { readonly emailAddress: string; readonly role: MemberRoleView },
  ): Promise<IssuedInvitationView>;
  revokeInvitation(workspaceId: string, invitationId: string): Promise<void>;
  setMemberRole(
    workspaceId: string,
    membershipId: string,
    role: MemberRoleView,
  ): Promise<WorkspaceMemberView>;
  removeMember(workspaceId: string, membershipId: string): Promise<void>;
  listApiKeys(): Promise<readonly ApiKeyView[]>;
  /** The returned key exists here and nowhere else. Never call this twice for one key. */
  createApiKey(draft: ApiKeyDraft): Promise<IssuedApiKeyView>;
  revokeApiKey(apiKeyId: string): Promise<void>;
  listWebhookEndpoints(): Promise<readonly WebhookEndpointView[]>;

  // --- the Test console (P11-09) -------------------------------------------------------------
  // `startVoiceTestCall`, `endVoiceTestCall`, `sendTestChatTurn` — `./slice/test-call.ts` (RB-42).

  // --- Agent · Channels (P11-04) --------------------------------------------------------
  listVoiceAgentChannelBindings(voiceAgentId: string): Promise<readonly ChannelBindingRowView[]>;
  getVoiceAgentConcurrency(voiceAgentId: string): Promise<ConcurrencySummaryView>;
  getVoiceAgentConnection(voiceAgentId: string): Promise<VoiceAgentConnectionView>;
  getVoiceAgentAnswering(voiceAgentId: string): Promise<AnsweringScheduleView>;
  listVoiceAgentSignallingEvents(voiceAgentId: string): Promise<readonly SignallingEventView[]>;
  /** Accepted, not applied. The worker rotates; the current password keeps working. */
  requestSipRotation(voiceAgentId: string): Promise<SipRotationView>;

  // --- Numbers and SIP (P11-05) ----------------------------------------------------------
  listNumbers(): Promise<NumberPageView>;
  /** numbers-sip-sync: one number's figures and its five latest conversations. */
  readNumberActivity(phoneNumberId: string): Promise<NumberActivityView>;
  listSipCredentials(): Promise<readonly WorkspaceSipCredentialView[]>;
  /** Measured by WeTalk. No carrier API reports region or latency. */
  getRegionLatency(): Promise<RegionLatencyView>;

  // --- Messaging (P11-06) -----------------------------------------------------------------
  listMessagingMedia(): Promise<readonly MessagingMediumRowView[]>;
  getMessagingMedium(mediumCode: string): Promise<MessagingMediumDetailView>;
  connectMessagingMedium(mediumCode: string, voiceAgentId: string): Promise<WebChatWidgetView>;
  disconnectMessagingMedium(mediumCode: string): Promise<void>;
  listWebChatConversations(): Promise<readonly WebChatConversationRowView[]>;

  // --- Agent · Analytics, Versions and Review (P11-01/02/03) ------------------------------
  getVoiceAgentAnalytics(
    voiceAgentId: string,
    range: AnalyticsRange,
  ): Promise<VoiceAgentAnalyticsView>;
  listAgentVersions(voiceAgentId: string): Promise<readonly AgentVersionSummaryView[]>;
  /**
   * Publish, and restore, which P7 built as **one handler** over
   * `UPDATE … WHERE live_version_seq = $expected`. `expectedLiveVersionSeq` is what makes a
   * lost update a 409 instead of a silent overwrite, so it is a required argument rather than
   * something the gateway reads for the caller.
   */
  publishAgentVersion(
    voiceAgentId: string,
    agentVersionId: string,
    expectedLiveVersionSeq: number,
  ): Promise<number>;
  restoreAgentVersion(
    voiceAgentId: string,
    agentVersionId: string,
    expectedLiveVersionSeq: number,
  ): Promise<number>;
  /**
   * The "Record calls" switch (plan-003 RC-47, `PATCH /v1/agents/{voice_agent_id}`). The setting
   * is pinned in a version, so changing it PUBLISHES one — which is why it takes the same
   * `expectedLiveVersionSeq` a publish does, and a stale one is the same 409.
   */
  setVoiceAgentRecording(
    voiceAgentId: string,
    recordingEnabled: boolean,
    expectedLiveVersionSeq: number,
  ): Promise<VoiceAgentRecordingView>;
  listVoiceAgentTestSuites(voiceAgentId: string): Promise<readonly TestSuiteView[]>;
  listGaps(voiceAgentId: string): Promise<readonly GapView[]>;
  /** Queues the answer into the next version. Publishes nothing. */
  answerGap(voiceAgentId: string, gapId: string, answerText: string): Promise<GapView>;
  dismissGap(voiceAgentId: string, gapId: string): Promise<GapView>;
  /**
   * Folds every queued answer into a new version and publishes it (2026-09-30). Takes the same
   * `expectedLiveVersionSeq` a publish does; a stale one is the same 409.
   */
  publishAnswers(voiceAgentId: string, expectedLiveVersionSeq: number): Promise<PublishedAnswersView>;
  listConversationFlags(voiceAgentId: string): Promise<readonly ConversationFlagView[]>;
  resolveConversationFlag(
    voiceAgentId: string,
    conversationFlagId: string,
  ): Promise<ConversationFlagView>;

  // --- P14: outbound, compliance and optimisation review -------------------------------------
  // Declared in their slices by declaration merging (plan-002 RB-03, IC-2), each owned by the
  // unit that closes it: `./slice/contact-list.ts` (RB-24), `./slice/campaign.ts` (RB-25),
  // `./slice/survey.ts` (RB-26), `./slice/do-not-call.ts` (RB-27, including `optOutPageLink`),
  // `./slice/compliance.ts` (RB-28), `./slice/audit-entry.ts` (RB-29) and
  // `./slice/optimisation-review.ts` (RB-30).

  // --- streams (documented paths, EventSource transport) --------------------------------
  streamBuildJob(buildJobId: string, onEvent: StreamListener): StreamSubscription;
  streamVoiceAgentLive(voiceAgentId: string, onEvent: StreamListener): StreamSubscription;
  streamConversationTranscript(conversationId: string, onEvent: StreamListener): StreamSubscription;
}

/** One framed SSE message, as `packages/domain`'s `SseMessage` carries it. */
export interface StreamFrame {
  readonly id: string;
  readonly event: string;
  readonly data: unknown;
}

export type StreamListener = (frame: StreamFrame) => void;

export interface StreamSubscription {
  close(): void;
}
